import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import PropertyCard from "../../../components/PropertyCard";
import type { Property } from "../../../global/types";
import { STYLE_GUIDE_MOCK_PROPERTIES } from "../styleGuideMockProperties";

const PROPERTIES: Property[] = STYLE_GUIDE_MOCK_PROPERTIES;

function StyleGuidePropertyCard({
  property,
  isInitiallyFavorite,
}: {
  property: Property;
  isInitiallyFavorite: boolean;
}) {
  return (
    <div className="mx-1 w-[236px] min-w-[236px] max-w-[236px] flex-[0_0_236px] mobile:w-[228px] mobile:min-w-[228px] mobile:max-w-[228px] tablet:mx-0 tablet:w-[calc(33.333%-9px)] tablet:min-w-[200px] tablet:max-w-[248px] tablet:flex-[0_0_calc(33.333%-9px)] desktop:w-[calc(25%-12px)] desktop:min-w-[200px] desktop:max-w-[252px] desktop:flex-[0_0_calc(25%-12px)] desktopXl:w-[calc(25%-12px)] desktopXl:min-w-[212px] desktopXl:max-w-[260px] desktopXl:flex-[0_0_calc(25%-12px)] desktopTxl:w-[256px] desktopTxl:min-w-[256px] desktopTxl:max-w-[256px] desktopTxl:flex-[0_0_256px]">
      <PropertyCard
        property={property}
        isInitiallyFavorite={isInitiallyFavorite}
        coverPlaceholder
      />
    </div>
  );
}

export function StyleGuidePropertyCarouselDemo() {
  const [activeDot, setActiveDot] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const history = useHistory();
  const carousselRef = useRef<HTMLDivElement | null>(null);

  function checkScroll() {
    const element = carousselRef.current;
    if (element) {
      setIsScrollable(element.scrollWidth > element.clientWidth);
    }
  }

  const setCarousselRef = useCallback((node: HTMLDivElement | null) => {
    carousselRef.current = node;
    if (node) {
      checkScroll();
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", checkScroll);
    const id = window.requestAnimationFrame(() => checkScroll());
    return () => {
      window.removeEventListener("resize", checkScroll);
      window.cancelAnimationFrame(id);
    };
  }, []);

  function handleSeeMoreProperties() {
    history.push({ pathname: "/style-guide", hash: "style-guide-slider" });
    window.requestAnimationFrame(() => {
      document.getElementById("style-guide-slider")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  function handleDotClick(dot: number) {
    if (activeDot === dot) return;

    const element = carousselRef.current;
    if (!element) return;

    const cardWidth = element.children[0]?.clientWidth || 300;
    const gap = 16;
    const totalCardWidth = cardWidth + gap;
    const scrollPosition = dot * totalCardWidth;

    element.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });

    setActiveDot(dot);
  }

  function handleScroll() {
    const element = carousselRef.current;
    if (!element) return;

    const { scrollLeft } = element;
    const cardWidth = element.children[0]?.clientWidth || 300;
    const gap = 16;
    const totalCardWidth = cardWidth + gap;
    const currentDot = Math.round(scrollLeft / totalCardWidth);
    setActiveDot(currentDot);
  }

  function handleScrollRight() {
    const element = carousselRef.current;
    if (!element) return;

    const { scrollLeft, clientWidth } = element;
    const gap = 16;

    element.scrollTo({
      left: scrollLeft + clientWidth - gap,
      behavior: "smooth",
    });
  }

  function handleScrollLeft() {
    const element = carousselRef.current;
    if (!element) return;

    const { scrollLeft, clientWidth } = element;
    const gap = 16;

    element.scrollTo({
      left: scrollLeft - clientWidth + gap,
      behavior: "smooth",
    });
  }

  const calculateDots = () => {
    if (!carousselRef.current || PROPERTIES.length <= 1) return 0;

    const element = carousselRef.current;
    const cardWidth = element.children[0]?.clientWidth || 300;
    const gap = 16;
    const cardsPerView = Math.floor(element.clientWidth / (cardWidth + gap));

    return Math.max(1, PROPERTIES.length - cardsPerView + 1);
  };

  const dotsCount = calculateDots();

  return (
    <div className="mt-3 desktop:mt-5 [&_.property-address-newcomers]:mt-[2px] [&_.property-address-newcomers]:text-[0.6875rem] [&_.property-detail-icon-newcomers]:h-[0.875rem] [&_.property-detail-icon-newcomers]:w-[0.875rem] [&_.property-detail-newcomers]:text-[0.6875rem] [&_.property-details-wrapper-newcomers]:mt-2 [&_.property-details-wrapper-newcomers]:pt-2 [&_.property-price-newcomers]:mb-[2px] [&_.property-price-newcomers]:text-[1.0625rem] [&_.property-title-newcomers]:mt-1 [&_.property-title-newcomers]:text-[0.875rem] [&_.cover-image-container]:h-[148px] mobile:[&_.cover-image-container]:h-[142px] tablet:[&_.cover-image-container]:h-[136px] tablet:[&_.property-price-newcomers]:text-base desktop:[&_.cover-image-container]:h-[140px] desktopXl:[&_.cover-image-container]:h-[146px] desktopTxl:[&_.cover-image-container]:h-[152px] desktopTxl:[&_.property-address-newcomers]:text-xs desktopTxl:[&_.property-price-newcomers]:text-[1.125rem] desktopTxl:[&_.property-title-newcomers]:text-[0.9375rem]">
      <div className="relative">
      <div className="flex flex-wrap items-center px-3">
        <h2 className="mb-1 mr-auto font-['Inter',sans-serif] text-base font-semibold leading-[1.2] text-[#383838] mobileXs:text-[1.125rem] tablet:mb-0 tablet:text-[1.0625rem] desktop:text-[1.125rem] desktopXl:text-[1.1875rem] desktopTxl:text-[1.25rem]">
          Slider
        </h2>
        <button
          type="button"
          onClick={handleSeeMoreProperties}
          className="flex w-full items-center py-1 font-['Inter',sans-serif] text-[0.8125rem] font-medium text-[#6FA1F7] opacity-90 transition-colors hover:text-[#6497E8] hover:opacity-100 mobileXs:w-auto mobileXs:text-sm tablet:text-[0.9375rem] desktop:text-base desktopTxl:text-[1.0625rem]"
        >
          Ver mais <ChevronRight className="ml-1 h-[0.875rem] w-[0.875rem] opacity-80" aria-hidden />
        </button>
      </div>

      <div className="relative mt-1 w-full">
        {isScrollable ? (
          <button
            type="button"
            aria-label="Anterior"
            onClick={handleScrollLeft}
            className="absolute left-[0.375rem] top-1/2 z-[2] hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-none bg-white p-0 text-[#DD6435] shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-all duration-200 hover:scale-[1.04] hover:shadow-[0_4px_16px_rgba(0,0,0,0.16)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6FA1F7] mobileSm:flex tablet:left-2 tablet:h-10 tablet:w-10"
          >
            <ChevronLeft className="h-[1.375rem] w-[1.375rem] shrink-0 tablet:h-6 tablet:w-6" aria-hidden />
          </button>
        ) : null}

        <div
          ref={setCarousselRef}
          onScroll={handleScroll}
          className="mt-4 flex gap-2 overflow-x-auto px-[10px] pb-[10px] [scrollbar-width:none] [scroll-snap-type:x_mandatory] [&::-webkit-scrollbar]:hidden tablet:gap-[10px] tablet:px-[14px] tablet:pb-3 desktop:gap-3 desktop:px-[18px] desktop:pb-[14px] desktopXl:gap-3 desktopXl:px-[22px] desktopXl:pb-4 desktopTxl:gap-3 desktopTxl:px-7 desktopTxl:pb-[18px]"
        >
          {PROPERTIES.map((property) => (
            <StyleGuidePropertyCard
              key={property.id}
              property={property}
              isInitiallyFavorite={false}
            />
          ))}
        </div>

        {isScrollable ? (
          <button
            type="button"
            aria-label="Seguinte"
            onClick={handleScrollRight}
            className="absolute right-[0.375rem] top-1/2 z-[2] hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-none bg-white p-0 text-[#DD6435] shadow-[0_2px_12px_rgba(0,0,0,0.12)] transition-all duration-200 hover:scale-[1.04] hover:shadow-[0_4px_16px_rgba(0,0,0,0.16)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6FA1F7] mobileSm:flex tablet:right-2 tablet:h-10 tablet:w-10"
          >
            <ChevronRight className="h-[1.375rem] w-[1.375rem] shrink-0 tablet:h-6 tablet:w-6" aria-hidden />
          </button>
        ) : null}

        {isScrollable && dotsCount > 1 ? (
          <div className="mt-[10px] flex justify-center gap-2 px-3 tablet:hidden">
            {Array.from({ length: dotsCount }).map((_, index) => (
              <button
                key={String(index)}
                type="button"
                aria-label={`Ir para slide ${index + 1}`}
                className={`h-2 w-2 rounded-full bg-[#D9D9D9] opacity-45 transition-all tablet:h-[9px] tablet:w-[9px] ${
                  activeDot === index ? "scale-110 bg-[#DD6435] opacity-100" : ""
                }`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        ) : null}
      </div>
      </div>
    </div>
  );
}
